import type { ReactElement } from "react"
import {
  CONTROLS,
  isBoundField,
  isDisplayOnlyField,
  type ModuleDefinitionField,
  type ModuleField,
} from "shared"
import { rendererFor } from "./controls"
import { drawnAs } from "./fields"
import type { FieldValue, FieldValues } from "./fieldValues"
import styles from "./ModuleForm.module.css"

/**
 * Renders a module's fields as the form a practitioner fills in.
 *
 * Values are keyed by field id, so a field keeps its value when it is
 * renamed. The caller owns them; this reports each change. A display-only
 * bound field is drawn read-only, with the value the caller gives it. A
 * message for a field, by its id, is shown under it.
 */
export function ModuleForm({
  fields,
  values,
  messages = {},
  onChange,
}: {
  fields: ModuleDefinitionField[]
  values: FieldValues
  messages?: Record<string, string>
  onChange: (id: string, value: FieldValue | undefined) => void
}) {
  if (fields.length === 0) {
    return <p className={styles.empty}>This module has no fields.</p>
  }
  return (
    <div className={styles.fields}>
      {fields.map((field) => (
        <ModuleFormField
          key={field.id}
          field={isBoundField(field) ? drawnAs(field) : field}
          readOnly={isDisplayOnlyField(field)}
          value={values[field.id]}
          error={messages[field.id]}
          onChange={(value) => onChange(field.id, value)}
        />
      ))}
    </div>
  )
}

/**
 * Draws one field with its control's own renderer. The registry has an entry
 * for every control, so there is no case to miss here.
 */
function ModuleFormField({
  field,
  readOnly,
  value,
  error,
  onChange,
}: {
  field: ModuleField
  readOnly: boolean
  value: FieldValue | undefined
  error: string | undefined
  onChange: (value: FieldValue | undefined) => void
}): ReactElement {
  const { Render } = rendererFor(field.control)
  return (
    <Render
      field={field}
      value={answerOf(field, value)}
      onChange={onChange}
      readOnly={readOnly}
      error={error}
    />
  )
}

/**
 * A stored answer as its field's control takes it. The server refuses one of
 * another kind (ADR-032 S), so one here is a fault to fail on, not a value to
 * draw.
 */
const answerOf = (field: ModuleField, value: FieldValue | undefined) =>
  value === undefined ? undefined : CONTROLS[field.control].answer.parse(value)
